import { useContext, useEffect, useRef } from "react";
import { notification } from "antd";
import NotificationCard from "@/components/cards/NotificationCard";
import { readNotices } from "@/api/notices";
import { useEmployees } from "@/context/EmployeesContext";
import { UserContext } from "@/context/UserContext";
import { playNotificationSound } from "@/utils/notificationSound";

// Хук для управления сокет-уведомлениями
export const useSocketNotifications = (
  socket,
  setRedDot,
  setUnreadCount,
  refreshUnreadCount
) => {
  const { updateEmployeeStatus, employees } = useEmployees();
  const { user } = useContext(UserContext);

  const handlersSetRef = useRef(false);

  // Обработчики сокета регистрируются один раз, их замыкания не обновляются,
  // поэтому актуальное значение настройки читаем через ref
  const soundEnabledRef = useRef(true);
  soundEnabledRef.current = user?.sound_notifications_enabled !== false; // по умолчанию включено, как в БД

  useEffect(() => {
    if (!socket || handlersSetRef.current) return;

    const handleNoticeClose = async (id) => {
      try {
        await readNotices([id]);
        notification.destroy(id);
      } catch (error) {
        console.error("Ошибка при закрытии уведомления:", error);
      }
    };

    const handleNoticeBan = (id) => {
      // TODO: Реализовать логику бана уведомлений
      console.log("Бан уведомления:", id);
    };

    const handleOpenNotice = (data) => {
      notification.open({
        duration: 30,
        closeIcon: false,
        className: "no-title",
        placement: "bottomRight",
        style: { width: 360, padding: 0 },
        key: data.uuid,
        description: (
          <NotificationCard
            data={{
              ...data,
              object: data.info,
              unit: data.info?.unit,
              object_type: data.info,
              info: { ...data.info, change_author: data.author },
            }}
            // Передаем employees как проп, чтобы не использовать контекст
            // Antd уведомления рендерятся через портал в отдельный DOM-элемент, который находится вне дерева React-компонентов, поэтому он не имеет доступа к контексту EmployeesProvider
            employees={employees}
            showBan
            onClose={() => handleNoticeClose(data.uuid)}
            onBan={() => handleNoticeBan(data.uuid)}
          />
        ),
      });
    };

    const handleEmployeeStatusUpdate = (employeeData) => {
      updateEmployeeStatus(employeeData);
    };

    const handleSocketMessage = (data, ...args) => {
      console.log("[SOCKET MESSAGE]", data, args);

      switch (data) {
        case "fire_on":
          setRedDot(true);
          break;
        case "fire_off":
          setRedDot(false);
          if (setUnreadCount) {
            setUnreadCount(0);
          }
          break;
        case "unread_count":
          const unreadPayload = args[0] || {};
          const normalizedUnreadCount = Math.max(
            0,
            Number(unreadPayload.count) || 0
          );
          if (setUnreadCount) {
            setUnreadCount(normalizedUnreadCount);
          }
          setRedDot(normalizedUnreadCount > 0);
          console.log("[SOCKET EVENT]", {
            event: "unread_count",
            unreadCount: normalizedUnreadCount,
            receivedAt: new Date().toISOString(),
          });
          break;
        case "notification":
          const notification = args[0];

          if (notification?.type === "message") {
            handleOpenNotice(notification);
            if (soundEnabledRef.current) {
              playNotificationSound();
            }
            if (refreshUnreadCount) {
              refreshUnreadCount();
            }
          }
          break;
        case "user_status_change":
          handleEmployeeStatusUpdate(args[0]);
          break;
        default:
          console.log("Неизвестное сообщение от сервера:", data, args);
          break;
      }
    };

    socket.onAny(handleSocketMessage);
    handlersSetRef.current = true;

    return () => {
      if (socket && handlersSetRef.current) {
        socket.offAny(handleSocketMessage);
        handlersSetRef.current = false;
      }
    };
    // eslint-disable-next-line
  }, [socket]);
};
